import { formatQuantity } from '../../utils/inventoryLabels';
import { normalizeText } from '../../utils/text';

export const ALL_CLIENTS = 'all';

/** The three tabs of the screen: the two sources of pending stock, and what was done. */
export const PUTAWAY_TAB = {
  RECEIVING: 'receiving',
  RESTOCK: 'restock',
  HISTORY: 'history',
};

export const EMPTY_PUTAWAY_COUNTS = {
  receiving: { rows: 0, units: 0 },
  restock: { rows: 0, units: 0 },
};

/** The columns of the putaway sheet. `Lote` only matters when the SKU sits on the dock in more than one lot. */
export const PUTAWAY_TEMPLATE_COLUMNS = ['SKU', 'Lote', 'Cantidad', 'Ubicación'];

/** One row by name and one by code, so the sheet shows that both work. */
export const PUTAWAY_TEMPLATE_EXAMPLE = [
  { SKU: 'POL-001', Lote: '', Cantidad: 50, 'Ubicación': 'A-01-01' },
  { SKU: 'TAZ-010', Lote: 'L-A', Cantidad: 20, 'Ubicación': 'A0102' },
];

/** Only these zones leave the stock available to sell: nothing is "put away" anywhere else. */
const DESTINATION_ZONES = ['storage', 'picking'];

export const formatPutawayMoment = (value) =>
  value
    ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

/** A date with no time, read as local: `new Date('2027-01-15')` alone would be the day before. */
export const formatPutawayDate = (value) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-CL') : '';

/** The active locations stock can be put away into. */
export const destinationSlots = (slots) =>
  slots.filter((slot) => DESTINATION_ZONES.includes(slot.zone_type));

/** Why the server suggests a location for a row, or nothing when it does not. */
export const reasonsFor = (row, slotId) =>
  (row?.suggestions || []).find((suggestion) => suggestion.slot_id === slotId)?.reasons || [];

/**
 * The destinations to offer for a row: the suggested ones first, in the
 * server's order, then the rest as they come (by walking rank).
 */
export const orderedDestinations = (row, destinations) => {
  const suggestedIds = (row?.suggestions || []).map((suggestion) => suggestion.slot_id);
  const suggested = suggestedIds
    .map((slotId) => destinations.find((slot) => slot.id === slotId))
    .filter(Boolean);
  const rest = destinations.filter((slot) => !suggestedIds.includes(slot.id));
  return [...suggested, ...rest];
};

/**
 * The location a typed or pasted text names, if it names one exactly.
 *
 * This is the scanner's path: the code arrives as text and nobody clicks. It
 * has to match a code or a name whole — a scanner that "finds" A-01 when the
 * label said A-010 puts the goods in the wrong rack.
 *
 * @param {string} typed
 * @param {Array<object>} destinations
 * @returns {object|null}
 */
export const resolveDestination = (typed, destinations) => {
  const key = normalizeText(typed).trim();
  if (!key) return null;
  return (
    destinations.find(
      (slot) => normalizeText(slot.code).trim() === key || normalizeText(slot.name).trim() === key
    ) || null
  );
};

/**
 * What is still missing before the row can be put away, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * @param {object} params
 * @param {string} params.quantity
 * @param {number} params.max - What there is to put away of that row.
 * @param {object|null} params.destination - The location picked or typed.
 * @param {string} params.typed - What is in the code field, to name it when it matches nothing.
 * @param {string} [params.warehouseName]
 */
export const putawayProblem = ({ quantity, max, destination, typed, warehouseName }) => {
  const amount = Number(quantity);
  if (!quantity || Number.isNaN(amount) || amount <= 0) return 'Indica cuánto guardar';
  if (amount > max) return `Solo hay ${formatQuantity(max)} por guardar`;
  if (!destination) {
    return typed.trim()
      ? `No existe «${typed.trim()}» en ${warehouseName || 'esta bodega'}`
      : 'Elige la ubicación destino';
  }
  return null;
};

/**
 * Which call puts a row away, and with what. For the screen it is one gesture;
 * by its source the row goes to one endpoint or the other.
 *
 * @returns {{isRestock: boolean, payload: object}}
 */
export const buildPutawayRequest = (row, { slotId, quantity, notes, warehouseId }) => {
  const trimmedNotes = (notes || '').trim() || undefined;

  if (row.source === PUTAWAY_TAB.RESTOCK) {
    return {
      isRestock: true,
      payload: {
        order_product_id: row.order_product,
        to_slot_id: slotId,
        quantity,
        lot: row.lot || '',
        notes: trimmedNotes,
      },
    };
  }

  return {
    isRestock: false,
    payload: {
      product_id: row.product,
      from_slot_id: row.slot,
      warehouse_id: warehouseId,
      to_slot_id: slotId,
      quantity,
      lot: row.lot || '',
      // The delivery note it came with, so the kardex can tell where it is from.
      reference: row.receipts?.[0] || undefined,
      notes: trimmedNotes,
    },
  };
};

/** The rows of a selection that the server suggested a destination for. */
export const rowsWithSuggestion = (rows) => (rows || []).filter((row) => row.suggested?.slot_id);

/** Units in a set of rows. */
export const unitsOf = (rows) => (rows || []).reduce((total, row) => total + Number(row.quantity || 0), 0);

/**
 * The bulk request: every row with its full quantity, to `slotId` when given
 * or to its own suggestion otherwise. One shape for both sources; the server
 * tells them apart by `source`.
 */
export const buildBulkPutawayRequest = (rows, { slotId = null, warehouseId, notes } = {}) => ({
  warehouse_id: warehouseId,
  notes: (notes || '').trim() || undefined,
  items: rows.map((row) => ({
    source: row.source === PUTAWAY_TAB.RESTOCK ? 'restock' : 'receiving',
    product_id: row.product,
    from_slot_id: row.slot ?? null,
    order_product_id: row.order_product ?? null,
    to_slot_id: slotId ?? row.suggested?.slot_id ?? null,
    quantity: row.quantity,
    lot: row.lot || '',
    reference: row.receipts?.[0] || '',
  })),
});

/**
 * The server's refused rows, named by SKU: "POL-001 (L-22): motivo". The
 * index comes back 1-based over the rows that were sent.
 */
export const bulkProblemLines = (failure, rows) =>
  (failure?.errors || []).map((item) => {
    const row = rows?.[item.index - 1];
    const who = row ? `${row.product_sku}${row.lot ? ` (${row.lot})` : ''}` : `Fila ${item.index}`;
    return `${who}: ${item.message}`;
  });

/** What to tell once a bulk save went through. */
export const bulkDoneNotice = (result, slotName = null) => {
  const where = slotName ? ` en ${slotName}` : ' en su ubicación sugerida';
  const warnings = result?.capacity_warnings || [];
  return {
    type: 'success',
    message: [`${result?.saved ?? 0} filas guardadas${where} · ${formatQuantity(result?.units ?? 0)} un.`, ...warnings].join(' '),
  };
};

/** "35 un de POL-001 guardadas en A-01-01." */
export const putawayDoneMessage = (row, quantity, slotName) =>
  `${formatQuantity(quantity)} ${row.base_uom || 'un'} de ${row.product_sku} guardadas en ${slotName}.`;

/* -------------------------------------------------------------------------- */
/*                                    Excel                                   */
/* -------------------------------------------------------------------------- */

/** The pending rows on screen as a sheet. */
export const buildPendingExportRows = (rows, tab) =>
  rows.map((row) => {
    const isRestock = tab === PUTAWAY_TAB.RESTOCK;
    return {
      Recibido: row.received_at ? formatPutawayMoment(row.received_at) : '',
      Cliente: row.company_name,
      SKU: row.product_sku,
      Producto: row.product_name,
      Lote: row.lot || '',
      Vence: row.expiry_date || '',
      [isRestock ? 'Orden' : 'Recepción']: isRestock
        ? `#${row.order}${row.order_reference ? ` · ${row.order_reference}` : ''}`
        : row.slot_name || 'Sin ubicación',
      Guías: (row.receipts || []).join(', '),
      'Por guardar': row.quantity,
      'Destino sugerido': row.suggested?.slot_name || '',
      Razones: (row.suggested?.reasons || []).join(' · '),
    };
  });

/** What was put away, as a sheet. */
export const buildHistoryExportRows = (rows) =>
  rows.map((row) => ({
    Fecha: formatPutawayMoment(row.occurred_at),
    Cliente: row.company_name,
    SKU: row.product_sku,
    Producto: row.product_name,
    Lote: row.lot || '',
    Desde: row.from_name,
    Hacia: row.to_name,
    Cantidad: row.quantity,
    Quién: row.actor_email || 'sistema',
    Origen: row.source === PUTAWAY_TAB.RESTOCK ? `Orden #${row.order}` : row.reference || '',
    Nota: row.notes || '',
  }));

const TAB_FILE_NAMES = {
  [PUTAWAY_TAB.RECEIVING]: 'desde-recepcion',
  [PUTAWAY_TAB.RESTOCK]: 'por-reponer',
  [PUTAWAY_TAB.HISTORY]: 'historial',
};

export const putawayExportFileName = (warehouse, tab) =>
  `almacenado-${TAB_FILE_NAMES[tab]}-${warehouse?.name || 'bodega'}.xlsx`;
